// frontend/src/features/ogdb2/components/ProductionDetailPopupWindow.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ProductionDetailModal from "./ProductionDetailModal";

/**
 * Opens a real popup window and renders ProductionDetailModal into it via a React portal.
 * IMPORTANT: we copy over all <link rel="stylesheet"> and <style> tags from the main app
 * so Tailwind / app CSS works inside the popup window.
 */
export default function ProductionDetailPopupWindow({
  open,
  onClose,
  unitId,
  prodYear,
  resolvedFacts,

  // ✅ NEW: "oil" | "gas"
  defaultTab,

  oilBasis,
  gasBasis,
  onSetOilBasis,
  onSetGasBasis,
}) {
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  const title = useMemo(() => {
    const id = unitId ? String(unitId) : "—";
    return `OGDB2 Production Detail — BAID ${id}`;
  }, [unitId]);

  function copyStylesToPopup(popupWindow) {
    try {
      const srcHead = document.head;
      const dstHead = popupWindow.document.head;

      // Clear any previous copied styles to prevent duplicates across re-open
      const existing = dstHead.querySelectorAll("[data-ogdb2-copied-style='1']");
      existing.forEach((n) => n.remove());

      // Copy <link rel="stylesheet"> and <style> tags
      const nodes = srcHead.querySelectorAll("link[rel='stylesheet'], style");
      nodes.forEach((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("data-ogdb2-copied-style", "1");
        dstHead.appendChild(clone);
      });

      // Also copy <meta charset> if needed
      const charset = srcHead.querySelector("meta[charset]");
      if (charset && !dstHead.querySelector("meta[charset]")) {
        const c = charset.cloneNode(true);
        c.setAttribute("data-ogdb2-copied-style", "1");
        dstHead.prepend(c);
      }
    } catch (e) {
      // If anything fails, popup will still render (just unstyled)
      console.warn("Failed to copy styles into popup:", e);
    }
  }

  useEffect(() => {
    if (!open) return;

    // Reuse a named window so clicking again focuses existing popup
    const w = window.open(
      "",
      "ogdb2_production_detail",
      "popup,width=1400,height=900,left=80,top=60"
    );

    if (!w) {
      window.alert("Popup blocked. Please allow popups for this site.");
      onClose?.();
      return;
    }

    popupRef.current = w;

    // Basic document shell
    w.document.title = title;
    w.document.body.style.margin = "0";
    w.document.body.style.background = "#f9fafb";

    // ✅ Critical: bring over Tailwind/app CSS
    copyStylesToPopup(w);

    // Create mount container
    let mount = w.document.getElementById("ogdb2-prod-root");
    if (!mount) {
      mount = w.document.createElement("div");
      mount.id = "ogdb2-prod-root";
      mount.style.height = "100vh";
      w.document.body.appendChild(mount);
    }

    containerRef.current = mount;
    setReady(true);

    // If user closes the window manually, reflect back to app state.
    const timer = window.setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        window.clearInterval(timer);
        popupRef.current = null;
        containerRef.current = null;
        setReady(false);
        onClose?.();
      }
    }, 400);

    return () => {
      window.clearInterval(timer);
      try {
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
      } catch {}
      popupRef.current = null;
      containerRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Keep popup title updated as selection changes
  useEffect(() => {
    const w = popupRef.current;
    if (!open || !w || w.closed) return;
    try {
      w.document.title = title;
    } catch {}
  }, [open, title]);

  if (!open || !ready || !containerRef.current) return null;

  return createPortal(
    <ProductionDetailModal
      open={true}
      onClose={onClose}
      unitId={unitId}
      prodYear={prodYear}
      resolvedFacts={resolvedFacts}
      defaultTab={defaultTab}
      oilBasis={oilBasis}
      gasBasis={gasBasis}
      onSetOilBasis={onSetOilBasis}
      onSetGasBasis={onSetGasBasis}
    />,
    containerRef.current
  );
}
